'use client';

import { CSSProperties, useState, useEffect, useMemo, useRef } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { getStrapiImageUrl } from '@/lib/image-utils';

interface ImageCarouselProps {
  images: { id: number; url: string; alternativeText?: string }[];
  containerClassName?: string;
}

export function ImageCarousel({ images, containerClassName }: ImageCarouselProps) {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [imageRatios, setImageRatios] = useState<Record<number, number>>({});
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchEndY, setTouchEndY] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateWidth = () => {
      setContainerWidth(element.clientWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  const safeIndex = images.length > 0 && currentIndex < images.length ? currentIndex : 0;
  const currentImage = images[safeIndex];
  const currentAspectRatio = currentImage ? imageRatios[currentImage.id] || 16 / 9 : 16 / 9;
  const imageHeight = useMemo(() => {
    if (containerWidth <= 0) return 360;
    return Math.min(600, Math.max(220, containerWidth / currentAspectRatio));
  }, [containerWidth, currentAspectRatio]);
  if (!images || images.length === 0) return null;

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev <= 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev >= images.length - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchEndX(null);
    setTouchEndY(null);
    setTouchStartX(e.targetTouches[0].clientX);
    setTouchStartY(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchEndX(e.targetTouches[0].clientX);
    setTouchEndY(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (touchStartX === null || touchEndX === null || touchStartY === null || touchEndY === null) return;
    const swipeDistance = touchStartX - touchEndX;
    const verticalDistance = Math.abs(touchStartY - touchEndY);
    const minSwipeDistance = 50;
    const maxVerticalDrift = 80;

    if (verticalDistance > maxVerticalDrift) return;

    if (swipeDistance > minSwipeDistance) {
      goToNext();
    } else if (swipeDistance < -minSwipeDistance) {
      goToPrevious();
    }
  };

  return (
    <div className={`relative w-full rounded-lg overflow-hidden${containerClassName ? ` ${containerClassName}` : ''}`}>
      {/* Main Image */}
      <div
        ref={containerRef}
        className="relative w-full h-[var(--image-height)] max-h-[600px] min-h-[220px]"
        style={{ '--image-height': `${imageHeight}px`, touchAction: 'pan-y' } as CSSProperties}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <Image
          src={getStrapiImageUrl(currentImage.url) || ''}
          alt={currentImage.alternativeText || `Image ${safeIndex + 1}`}
          fill
          className="object-contain"
          unoptimized
          onLoadingComplete={(img) => {
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
              setImageRatios((prev) => ({
                ...prev,
                [currentImage.id]: img.naturalWidth / img.naturalHeight,
              }));
            }
          }}
        />

        {/* Navigation Arrows - Only show if more than 1 image */}
        {images.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              onKeyDown={(e) => handleKeyDown(e, goToPrevious)}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition focus:outline-none focus:ring-2 focus:ring-white"
              aria-label={t.imageCarousel.previousImage}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={goToNext}
              onKeyDown={(e) => handleKeyDown(e, goToNext)}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition focus:outline-none focus:ring-2 focus:ring-white"
              aria-label={t.imageCarousel.nextImage}
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
            {safeIndex + 1} / {images.length}
          </div>
        )}
      </div>

    </div>
  );
}


